import { API_CONFIG } from "../config";
import {
  ApiResponse,
  Listing,
  ListingFilters,
  ListingsResponse,
  SavedListing,
} from "../types";
import api from "./base";

// Helper function to ensure content-type headers
const getJsonHeaders = () => ({
  headers: {
    "Content-Type": "application/json",
  },
});

export const listingsApi = {
  // Get all listings with optional filters
  getListings: async (
    filters: ListingFilters = {}
  ): Promise<ApiResponse<ListingsResponse>> => {
    const response = await api.get(API_CONFIG.ENDPOINTS.LISTINGS, {
      params: filters,
    });
    return response.data;
  },

  // Get a single listing by ID
  getListing: async (
    id: string | number
  ): Promise<ApiResponse<{ listing: Listing }>> => {
    const response = await api.get(
      API_CONFIG.ENDPOINTS.LISTING_DETAIL(id.toString())
    );
    return response.data;
  },

  // Create a new listing
  createListing: async (
    listing: Partial<Listing>
  ): Promise<ApiResponse<Listing>> => {
    console.log("Creating listing:", listing);
    const response = await api.post(
      API_CONFIG.ENDPOINTS.LISTINGS,
      listing, // Send data directly, not as a string
      getJsonHeaders()
    );
    return response.data;
    console.log("Listing created:", response.data);
  },

  // Update an existing listing
  updateListing: async (
    id: string | number,
    listing: Partial<Listing>
  ): Promise<ApiResponse<Listing>> => {
    const response = await api.put(
      API_CONFIG.ENDPOINTS.LISTING_DETAIL(id.toString()),
      listing, // Send data directly, not as a string
      getJsonHeaders()
    );
    return response.data;
  },

  // Delete a listing
  deleteListing: async (id: string | number): Promise<ApiResponse<any>> => {
    const response = await api.delete(
      API_CONFIG.ENDPOINTS.LISTING_DETAIL(id.toString())
    );
    return response.data;
  },

  // Boost a listing
  boostListing: async (
    id: string | number,
    boostOption: string
  ): Promise<
    ApiResponse<{
      success: boolean;
      listing: Listing;
      pointsUsed: number;
      remainingPoints: number;
      boostedUntil: string;
    }>
  > => {
    const response = await api.post(
      API_CONFIG.ENDPOINTS.BOOST_LISTING(id.toString()),
      { boostOption }, // Send as object, not string
      getJsonHeaders()
    );
    return response.data;
  },

  // Feature a listing
  featureListing: async (
    id: string | number,
    featureOption: string
  ): Promise<
    ApiResponse<{
      success: boolean;
      listing: Listing;
      pointsUsed: number;
      remainingPoints: number;
      featuredUntil: string;
    }>
  > => {
    const response = await api.post(
      API_CONFIG.ENDPOINTS.FEATURE_LISTING(id.toString()),
      { featureOption }, // Send as object, not string
      getJsonHeaders()
    );
    return response.data;
  },

  // Get listings for current user
  getUserListings: async (
    userId: string | number,
    filters: ListingFilters = {}
  ): Promise<ApiResponse<ListingsResponse>> => {
    filters.user_id = Number(userId); // Corrected from userId to user_id
    const response = await api.get(API_CONFIG.ENDPOINTS.LISTINGS, {
      params: filters,
    });
    return response.data;
  },

  // Save a listing
  saveListing: async (
    listingId: number | string
  ): Promise<ApiResponse<SavedListing>> => {
    const response = await api.post(
      API_CONFIG.ENDPOINTS.SAVED_LISTINGS,
      { listingId: Number(listingId) }, // Send as object, not string
      getJsonHeaders()
    );
    return response.data;
  },

  // Remove a saved listing
  removeSavedListing: async (
    id: number | string
  ): Promise<ApiResponse<any>> => {
    const response = await api.delete(
      API_CONFIG.ENDPOINTS.REMOVE_SAVED_LISTING(id.toString())
    );
    return response.data;
  },

  // Get user's saved listings
  getSavedListings: async (): Promise<
    ApiResponse<{ data: SavedListing[] }>
  > => {
    const response = await api.get(API_CONFIG.ENDPOINTS.SAVED_LISTINGS);
    return response.data;
  },
};

// Re-export for backward compatibility
export const useListings = () => {
  return { getListings: listingsApi.getListings };
};

export const useListing = (id: string | number) => {
  return { getListing: () => listingsApi.getListing(id) };
};
